import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Copy, Download, Pencil, Trash2, Share2, Check, ExternalLink,
  ShieldCheck, Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

/**
 * AdDetailModal — full view + quick-edit + 6-action toolbar for a saved ad.
 *
 * Props:
 *   ad: the saved-ad object (id, headline, body, cta, hashtags, image, ...)
 *   onClose: () => void
 *   onUpdate(updatedAd): called after a successful inline save, so the
 *     Dashboard list updates without a full re-fetch.
 *   onDelete(id): called after a successful delete.
 */
const AdDetailModal = ({ ad, onClose, onUpdate, onDelete }) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => ({
    headline: ad.headline || '',
    body: ad.body || '',
    cta: ad.cta || '',
    hashtags: ad.hashtags || '',
  }));
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Close on Escape, lock body scroll while open
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const buildShareText = () => {
    const a = isEditing ? draft : ad;
    return `${a.headline || ''}\n\n${a.body || ''}\n\n${a.hashtags || ''}`.trim();
  };

  const dataUrlToFile = async (dataUrl, filename) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || 'image/png' });
  };

  // ---- Actions ----------------------------------------------------- //
  const handleCopyText = async () => {
    await navigator.clipboard.writeText(buildShareText());
    toast({ title: 'Caption copied', description: 'Paste anywhere you like.' });
  };

  const handleCopyCaptionAndImage = async () => {
    const text = buildShareText();
    await navigator.clipboard.writeText(text);
    if (ad.image) {
      try {
        const file = await dataUrlToFile(ad.image, `mbap-ad-${Date.now()}.png`);
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast({
          title: 'Caption + image ready',
          description: 'Caption copied. Image downloaded — attach it when you paste.',
        });
        return;
      } catch {
        // fall through to text-only message
      }
    }
    toast({ title: 'Caption copied', description: 'No image attached to this ad.' });
  };

  const handleDownloadImage = async () => {
    if (!ad.image) {
      toast({ title: 'No image', description: 'This ad has no image attached.' });
      return;
    }
    try {
      const file = await dataUrlToFile(ad.image, `mbap-ad-${Date.now()}.png`);
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: 'Image downloaded' });
    } catch {
      toast({ title: 'Download failed', description: 'Long-press the preview image to save instead.' });
    }
  };

  const handleShare = async () => {
    const text = buildShareText();
    try {
      if (ad.image && navigator.canShare) {
        const file = await dataUrlToFile(ad.image, 'ad-image.png');
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: ad.headline || 'My ad', text });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: ad.headline || 'My ad', text });
        return;
      }
      // Desktop fallback: copy + download
      await handleCopyCaptionAndImage();
    } catch (e) {
      if (e?.name === 'AbortError') return; // user cancelled
      toast({ title: 'Share not supported', description: 'Use Copy text + Download image instead.' });
    }
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      const payload = {
        ...ad,
        headline: draft.headline,
        body: draft.body,
        cta: draft.cta,
        hashtags: draft.hashtags,
      };
      delete payload.id;
      delete payload.userId;
      delete payload.createdAt;
      delete payload.updatedAt;
      const { data } = await api.put(`/ads/${ad.id}`, payload);
      onUpdate?.(data);
      setIsEditing(false);
      toast({ title: 'Changes saved' });
    } catch (err) {
      toast({
        title: 'Could not save',
        description: err?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenInCreate = () => navigate(`/create?adId=${ad.id}`);

  const handleDeleteConfirmed = async () => {
    try {
      await api.delete(`/ads/${ad.id}`);
      onDelete?.(ad.id);
      toast({ title: 'Ad deleted' });
      onClose();
    } catch {
      toast({ title: 'Could not delete', description: 'Please try again.' });
    }
  };

  // ---- Render ------------------------------------------------------ //
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="ad-detail-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8 overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="ad-detail-modal"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/90 hover:bg-white shadow-md text-slate-600 hover:text-slate-900"
          aria-label="Close"
          data-testid="ad-detail-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid md:grid-cols-2">
          {/* Image */}
          <div className="bg-slate-100 min-h-[280px] md:min-h-[480px] flex items-center justify-center">
            {ad.image ? (
              <img
                src={ad.image}
                alt={ad.headline || 'Ad image'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-slate-400 text-sm">No image</div>
            )}
          </div>

          {/* Body */}
          <div className="p-6 flex flex-col">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-xs border-slate-200 text-slate-600">
                {ad.businessType || 'business'}
              </Badge>
              {ad.complianceCheck?.passed && (
                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> UK-compliant
                </span>
              )}
            </div>

            {/* Editable fields */}
            <div className="mt-4 space-y-3 flex-1">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Headline</label>
                {isEditing ? (
                  <Input
                    value={draft.headline}
                    onChange={(e) => setDraft(d => ({ ...d, headline: e.target.value }))}
                    className="mt-1"
                    data-testid="ad-edit-headline"
                  />
                ) : (
                  <h3 className="mt-1 text-xl font-bold text-slate-900">{ad.headline}</h3>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Body</label>
                {isEditing ? (
                  <Textarea
                    value={draft.body}
                    onChange={(e) => setDraft(d => ({ ...d, body: e.target.value }))}
                    rows={5}
                    className="mt-1"
                    data-testid="ad-edit-body"
                  />
                ) : (
                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{ad.body}</p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Call to action</label>
                {isEditing ? (
                  <Input
                    value={draft.cta}
                    onChange={(e) => setDraft(d => ({ ...d, cta: e.target.value }))}
                    className="mt-1"
                    data-testid="ad-edit-cta"
                  />
                ) : (
                  <p className="mt-1 text-sm font-semibold text-orange-600">{ad.cta}</p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hashtags</label>
                {isEditing ? (
                  <Input
                    value={draft.hashtags}
                    onChange={(e) => setDraft(d => ({ ...d, hashtags: e.target.value }))}
                    className="mt-1"
                    data-testid="ad-edit-hashtags"
                  />
                ) : (
                  <p className="mt-1 text-sm text-slate-500">{ad.hashtags}</p>
                )}
              </div>
            </div>

            {/* Action toolbar */}
            <div className="mt-5 pt-5 border-t border-slate-200">
              {confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-slate-700">Delete this ad permanently?</span>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)} data-testid="ad-delete-cancel">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleDeleteConfirmed} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="ad-delete-confirm">
                    Yes, delete
                  </Button>
                </div>
              ) : isEditing ? (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setDraft({ headline: ad.headline, body: ad.body, cta: ad.cta, hashtags: ad.hashtags }); setIsEditing(false); }} data-testid="ad-edit-cancel">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveEdits} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ad-edit-save">
                    {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                    Save changes
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopyText} data-testid="ad-action-copy-text">
                    <Copy className="w-4 h-4 mr-1.5" /> Copy text
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopyCaptionAndImage} data-testid="ad-action-copy-all">
                    <Copy className="w-4 h-4 mr-1.5" /> Copy + image
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadImage} data-testid="ad-action-download">
                    <Download className="w-4 h-4 mr-1.5" /> Download
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="ad-action-edit">
                    <Pencil className="w-4 h-4 mr-1.5" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleShare} data-testid="ad-action-share">
                    <Share2 className="w-4 h-4 mr-1.5" /> Share
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} className="text-rose-600 hover:text-rose-700" data-testid="ad-action-delete">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                </div>
              )}
            </div>

            {!isEditing && !confirmDelete && (
              <button
                onClick={handleOpenInCreate}
                className="mt-3 text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
                data-testid="ad-action-open-in-create"
              >
                Need a full re-generate? Open in Create <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdDetailModal;
